import { useEffect, useRef, type ReactNode } from 'react';

// Padrão único de sobreposição do protótipo: drawer lateral de 540px + scrim
// (proto:112-118, classe de estado `.on`). Esta base renomeou o estado para
// `.open` (tokens.css:651-688) — usamos `.open`, não `.on`.
export function Drawer({
  aberto,
  titulo,
  aoFechar,
  children,
  rodape,
}: {
  aberto: boolean;
  titulo: string;
  aoFechar: () => void;
  children: ReactNode;
  rodape?: ReactNode;
}) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const elementoAnterior = useRef<Element | null>(null);
  const estavaAberto = useRef(false);

  // Captura o foco anterior durante a renderização (antes do commit), na
  // borda falso→verdadeiro de `aberto`. Nesse momento o DOM real ainda não
  // mudou, então `document.activeElement` é o elemento de fora do drawer —
  // capturar isso dentro do efeito abaixo seria tarde demais, porque o
  // `autoFocus` de um campo do `.db` já teria roubado o foco na fase de
  // commit (ver comentário no efeito).
  if (aberto && !estavaAberto.current) {
    elementoAnterior.current = document.activeElement;
  }
  estavaAberto.current = aberto;

  // Move o foco para dentro do drawer ao abrir e devolve ao elemento que
  // tinha foco antes, ao fechar (a11y — foco não pode "vazar" para trás do
  // scrim nem se perder na abertura).
  //
  // Se algum campo do `.db` tiver `autoFocus`, o React já o focou durante a
  // fase de commit, antes deste efeito rodar — nesse caso respeitamos esse
  // foco em vez de tomá-lo de volta para o contêiner do drawer.
  useEffect(() => {
    if (!aberto) return;

    const focoJaDentro =
      drawerRef.current?.contains(document.activeElement) &&
      document.activeElement !== drawerRef.current;
    if (!focoJaDentro) {
      drawerRef.current?.focus();
    }

    return () => {
      if (elementoAnterior.current instanceof HTMLElement) {
        elementoAnterior.current.focus();
      }
    };
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;

    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
    };

    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [aberto, aoFechar]);

  if (!aberto) return null;

  return (
    <>
      <div className="scrim open" onClick={aoFechar} />
      <div
        ref={drawerRef}
        className="drawer open"
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        tabIndex={-1}
      >
        <div className="dh">
          <h2>{titulo}</h2>
          <button type="button" className="btn sm" onClick={aoFechar}>
            ✕
          </button>
        </div>
        <div className="db">{children}</div>
        {rodape && <div className="df">{rodape}</div>}
      </div>
    </>
  );
}
